import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

interface LoginState {
  step: 'email' | 'verification';
  email: string;
  verificationCode: string;
}

export const Login: React.FC = () => {
  const history = useHistory();
  const [state, setState] = useState<LoginState>({
    step: 'email',
    email: '',
    verificationCode: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Ultra-defensive input handlers with useCallback
  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('Email change triggered, event:', e);
    console.log('Event target:', e?.target);
    console.log('Event target value:', e?.target?.value);
    
    if (!e || !e.target || typeof e.target.value !== 'string') {
      console.error('Invalid event or target');
      return;
    }
    
    const value = e.target.value;
    console.log('Setting email to:', value);
    
    setState(prevState => {
      console.log('Previous state:', prevState);
      const newState = { ...prevState, email: value };
      console.log('New state:', newState);
      return newState;
    });
  }, []);

  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e || !e.target || typeof e.target.value !== 'string') {
      console.error('Invalid verification code event');
      return;
    }
    
    const value = e.target.value;
    setState(prevState => ({ ...prevState, verificationCode: value }));
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.email) return;
    
    setIsLoading(true);
    setError('');
    
    // Simulate API call to send verification code
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsLoading(false);
    
    setState(prev => ({ ...prev, step: 'verification' }));
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.verificationCode) return;
    
    setIsLoading(true);
    setError('');
    
    // Simulate verification
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsLoading(false);
    
    // Simulate successful login
    localStorage.setItem('user', JSON.stringify({
      email: state.email,
      username: state.email.split('@')[0]
    }));
    
    history.push('/dashboard');
  };

  const handleDemoLogin = async () => {
    setIsLoading(true);
    setError('');
    
    // Simulate quick demo login
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    localStorage.setItem('user', JSON.stringify({
      email: 'demo@example.com',
      username: 'demo'
    }));
    
    setIsLoading(false);
    history.push('/dashboard');
  };

  const renderEmailStep = () => (
    <div className="auth-step">
      <div className="step-header">
        <h2>Welcome Back</h2>
        <p>Enter your email to receive a verification code</p>
      </div>
      
      <form onSubmit={handleEmailSubmit}>
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            id="email"
            type="email"
            value={state.email}
            onChange={handleEmailChange}
            placeholder="Enter your email"
            required
            autoFocus
          />
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <button type="submit" disabled={isLoading} className="auth-button">
          {isLoading ? 'Sending Code...' : 'Send Verification Code'}
        </button>
      </form>
      
      <div className="auth-divider">
        <span>or</span>
      </div>
      
      <button 
        onClick={handleDemoLogin} 
        disabled={isLoading}
        className="auth-button-secondary"
      >
        {isLoading ? 'Loading Demo...' : 'Try Demo Account'}
      </button>
    </div>
  );

  const renderVerificationStep = () => (
    <div className="auth-step">
      <div className="step-header">
        <h2>Check Your Email</h2>
        <p>Enter the verification code sent to {state.email}</p>
      </div>
      
      <form onSubmit={handleVerificationSubmit}>
        <div className="form-group">
          <label htmlFor="code">Verification Code</label>
          <input
            id="code"
            type="text"
            value={state.verificationCode}
            onChange={handleCodeChange}
            placeholder="Enter 6-digit code"
            maxLength={6}
            required
            autoFocus
          />
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <button type="submit" disabled={isLoading} className="auth-button">
          {isLoading ? 'Verifying...' : 'Sign In'}
        </button>
        
        <button 
          type="button" 
          onClick={() => setState(prev => ({ ...prev, step: 'email' }))}
          className="auth-button-secondary"
        >
          Back to Email
        </button>
      </form>
    </div>
  );

  return (
    <div className="auth-page">
      <div className="auth-container">
        {state.step === 'email' ? renderEmailStep() : renderVerificationStep()}
        
        <div className="auth-footer">
          <p>Don't have an account? <a href="/signup">Sign up</a></p>
          <p><a href="/">← Back to Home</a></p>
        </div>
      </div>
    </div>
  );
};